// Generic, config-driven questionnaire engine.
//
// Mirrors PCI's classic-ASP questionnaire engine (QuestionnaireRenderer.asp:
// HandleQuestion / HandleQuestionGroup / AutoSetAnswersBasedOnPoints) so that
// ANY questionnaire (Classification, Medical, Gang, Mental Health, ...) and
// ANY question within it gets points, auto-set, hilite and booking-queue-step
// handling for free, purely from config data (questionSideEffects /
// questionAutoSet / questionAttribs) - no per-question or per-questionnaire
// code required.
//
// src/util/questionnaireFlagEngine.js is the sibling module for Set/Clear
// Jacket Flag side effects and questionnaire/group score-threshold triggers -
// it reuses the low-level helpers exported here so there's one definition of
// "does this side effect fire."

import { REF_LIST_CODE } from "@/util/refList";
import { isRemoveFromQueueStep } from "@/util/common_function";

const {
  ADD_POINTS,
  SUBTRACT_POINTS,
  HILITE_ON,
  HILITE_OFF,
  SET_QUEUE_STEP,
  CLEAR_QUEUE_STEP,
} = REF_LIST_CODE.QUESTION_SIDE_EFFECT_TYPE;

const { INTERFACE_TYPES } = REF_LIST_CODE;

export const hasValue = (v) => v !== undefined && v !== null && v !== "";

// A question's side effects may come back as `questionSideEffects` (the live
// API shape) or `points` (the static fallback data in
// src/util/classification_questions.js) - accept either.
export const getSideEffects = (question) =>
  question?.questionSideEffects ?? question?.points ?? [];

export const findAnswer = (questionAnswers, questionId) =>
  questionAnswers?.find((qa) => qa?.questionId === questionId)?.answer;

// Mirrors PCI's fTrigger check in QuestionnaireRenderer.asp/HandleQuestion:
// answerToTriggerOnID === 0 (or absent) means "fires when the answer is
// cleared"; anything else must numerically match the current answer (ref-list
// item id).
export const sideEffectFires = (sideEffect, answer) => {
  const triggerOn = sideEffect?.answerToTriggerOnID;
  if (!hasValue(triggerOn) || triggerOn === 0) {
    return !hasValue(answer);
  }
  if (!hasValue(answer)) return false;
  return Number(answer) === Number(triggerOn);
};

// Single source of truth for "is this question currently shown" - based on
// dependsOnQuestionID / dependsOnQuestionAnswerRefListItemID / showOnlyIf.
// Used for rendering and to gate auto-set (mirrors ASP's fQuestionVisible
// check in AutoSetAnswersBasedOnPoints, which gates AutoSet only - points/
// flags/queue-step/hilite side effects fire off the stored answer regardless
// of visibility, matching ASP's HandleQuestion).
export const isQuestionVisible = (question, questionAnswers) => {
  if (!hasValue(question?.dependsOnQuestionID)) return true;
  if (!question?.showOnlyIf) return false;
  const answer = findAnswer(questionAnswers, question.dependsOnQuestionID);
  return (
    hasValue(answer) && answer === question?.dependsOnQuestionAnswerRefListItemID
  );
};

/**
 * Full recompute of points from scratch - no incremental deltas, so there's
 * nothing to drift out of sync as answers change. For each question, sums
 * numberOfPoints from any firing side effect whose questionSideEffectTypeID
 * is strictly Add Points or Subtract Points (subtracted points negated) -
 * other side-effect types (Set/Clear Flag, Set/Clear Queue Step, Hilite)
 * never contribute points, regardless of what's in their numberOfPoints field.
 *
 * Returns a shape that's wire-compatible with what the questionnaire list
 * components already pass into evaluateAndApplyQuestionnaireFlags.
 */
export const computePoints = ({ questionnaire, questionAnswers }) => {
  const pointsByQuestionId = {};
  const questionGroupPoints = [];
  let totalPoints = 0;

  questionnaire?.questionGroups?.forEach((group) => {
    let groupPoints = 0;

    group?.questions?.forEach((question) => {
      const sideEffects = getSideEffects(question);
      let questionPoints = 0;

      if (sideEffects?.length) {
        const answer = findAnswer(questionAnswers, question.questionID);

        sideEffects.forEach((sideEffect) => {
          const typeID =
            sideEffect?.questionSideEffectTypeID ??
            sideEffect?.QuestionSideEffectType_ID;
          if (typeID !== ADD_POINTS && typeID !== SUBTRACT_POINTS) return;
          if (!sideEffectFires(sideEffect, answer)) return;

          const pts =
            Number(sideEffect?.numberOfPoints ?? sideEffect?.NumberOfPoints) ||
            0;
          questionPoints += typeID === SUBTRACT_POINTS ? -pts : pts;
        });
      }

      pointsByQuestionId[question.questionID] = questionPoints;
      groupPoints += questionPoints;
    });

    questionGroupPoints.push({
      questionGroupId: group.questionGroupID,
      totalPoints: groupPoints,
    });
    totalPoints += groupPoints;
  });

  return { totalPoints, questionGroupPoints, pointsByQuestionId };
};

// Reads the right answer field off an AutoSet row for the target question's
// interface type - mirrors ASP's Select Case on current.InterfaceType in
// AutoSetAnswersBasedOnPoints.
const getAutoSetAnswer = (question, row) => {
  switch (question?.interfaceTypeID) {
    case INTERFACE_TYPES.TEXTBOX:
    case INTERFACE_TYPES.BIGTEXT:
      return row?.answer;
    case INTERFACE_TYPES.DATE:
      return row?.answerDate;
    case INTERFACE_TYPES.YESNO:
    case INTERFACE_TYPES.SELECT:
    default:
      return row?.answerRefListItemID;
  }
};

const autoSetRowApplies = (row, { totalPoints, questionGroupPoints }) => {
  const points = row?.questionnaireTotalPoints
    ? Number(totalPoints || 0)
    : Number(
        questionGroupPoints?.find(
          (qgp) => qgp.questionGroupId === row?.questionGroupID,
        )?.totalPoints || 0,
      );

  const hasMin = hasValue(row?.minPoints);
  const hasMax = hasValue(row?.maxPoints);

  if (!hasMin && !hasMax) return true;
  if (!hasMin) return points <= Number(row.maxPoints);
  if (!hasMax) return points >= Number(row.minPoints);
  return points >= Number(row.minPoints) && points <= Number(row.maxPoints);
};

/**
 * Generic port of ASP's AutoSetAnswersBasedOnPoints. For every currently
 * visible question with a non-empty questionAutoSet, walks its rows in
 * `sequence` order and takes the first enabled row whose relevant point value
 * (questionnaireTotalPoints ? totalPoints : that row's questionGroupID's
 * group points) falls within [minPoints, maxPoints] (open-ended handling
 * identical to ASP). No question-ID or questionnaire-name check anywhere -
 * driven purely by each question's own questionAutoSet config.
 */
export const computeAutoSetAnswers = ({
  questionnaire,
  questionAnswers,
  totalPoints,
  questionGroupPoints,
}) => {
  const results = [];

  questionnaire?.questionGroups?.forEach((group) => {
    group?.questions?.forEach((question) => {
      const rows = question?.questionAutoSet;
      if (!rows?.length) return;
      if (!isQuestionVisible(question, questionAnswers)) return;

      const sorted = [...rows].sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0),
      );

      const match = sorted.find(
        (row) =>
          row?.enabled !== false &&
          autoSetRowApplies(row, { totalPoints, questionGroupPoints }),
      );
      if (!match) return;

      results.push({
        questionId: question.questionID,
        answer: getAutoSetAnswer(question, match),
        forced: Boolean(match?.forced),
      });
    });
  });

  return results;
};

/**
 * Hilite On/Off side effects - last firing effect per question wins, matching
 * ASP's inline style.visibility toggling order (each side effect in the
 * array is processed in turn, later ones overwrite earlier ones).
 */
export const computeHilites = ({ questionnaire, questionAnswers }) => {
  const hilites = {};

  questionnaire?.questionGroups?.forEach((group) => {
    group?.questions?.forEach((question) => {
      const sideEffects = getSideEffects(question);
      if (!sideEffects?.length) return;

      const answer = findAnswer(questionAnswers, question.questionID);

      sideEffects.forEach((sideEffect) => {
        const typeID =
          sideEffect?.questionSideEffectTypeID ??
          sideEffect?.QuestionSideEffectType_ID;
        if (typeID !== HILITE_ON && typeID !== HILITE_OFF) return;
        if (!sideEffectFires(sideEffect, answer)) return;

        hilites[question.questionID] = typeID === HILITE_ON;
      });
    });
  });

  return hilites;
};

// Generic Name/Value lookup for a question's questionAttribs - no invented
// per-name behavior; whoever adds a new attribName later reads it with this.
export const getQuestionAttrib = (question, attribName) =>
  question?.questionAttribs?.find((a) => a?.attribName === attribName)
    ?.attribValue;

/**
 * Walks every question's Set/Clear Queue Step side effects against the
 * current answers. Only meaningful at Save, matching ASP's fValidating-only
 * gate for this side-effect type (Set/Clear Queue Step only fires on final
 * validation, unlike points/flags which react as you go).
 */
export const collectQueueStepDescriptors = ({
  questionnaire,
  questionAnswers,
}) => {
  const descriptors = [];

  questionnaire?.questionGroups?.forEach((group) => {
    group?.questions?.forEach((question) => {
      const sideEffects = getSideEffects(question);
      if (!sideEffects?.length) return;

      const answer = findAnswer(questionAnswers, question.questionID);

      sideEffects.forEach((sideEffect) => {
        const typeID =
          sideEffect?.questionSideEffectTypeID ??
          sideEffect?.QuestionSideEffectType_ID;
        if (typeID !== SET_QUEUE_STEP && typeID !== CLEAR_QUEUE_STEP) return;
        if (!sideEffectFires(sideEffect, answer)) return;

        const queStepID = sideEffect?.queStepID ?? sideEffect?.QueStepID;
        if (!hasValue(queStepID)) return;

        descriptors.push({
          action: typeID === SET_QUEUE_STEP ? "set" : "clear",
          queStepID,
          questionID: question.questionID,
        });
      });
    });
  });

  return descriptors;
};

// Applies queue-step descriptors to the booking's current
// bookingQueueDetails array - same row shape BookingQueueDetails/index.jsx's
// handleSave already writes. Set: upserts a completed row (mirrors ASP's
// UpdateBookingQueueStep(queStepID, True, "Automatically Completed.")). Clear:
// if a row for that step already exists, marks it incomplete (mirrors ASP's
// UpdateBookingQueueStep(queStepID, False, "Automatically un-completed."));
// no-ops if no row exists yet, same as ASP.
export const applyQueueStepDescriptors = (
  bookingQueueDetails,
  descriptors,
  context,
) => {
  let rows = [...(bookingQueueDetails || [])];

  descriptors.forEach((descriptor) => {
    const idx = rows.findIndex((row) => row?.queStepID === descriptor.queStepID);

    if (descriptor.action === "set") {
      const row = {
        queStepID: descriptor.queStepID,
        complete: true,
        completionDate: context?.completionDate,
        completionTime: context?.completionTime,
        completionStaffID: context?.completionStaffID,
        creatorStaffID: context?.creatorStaffID ?? context?.completionStaffID,
        comments: "Automatically Completed.",
        finalStep: isRemoveFromQueueStep(descriptor.queStepID),
      };
      rows = idx === -1 ? [...rows, row] : rows.map((r, i) => (i === idx ? { ...r, ...row } : r));
    } else if (idx !== -1) {
      rows = rows.map((r, i) =>
        i === idx
          ? { ...r, complete: false, comments: "Automatically un-completed." }
          : r,
      );
    }
  });

  return rows;
};

export const evaluateAndApplyQueueStepEffects = ({
  questionnaire,
  questionAnswers,
  bookingQueueDetails,
  context,
}) => {
  if (!questionnaire) return { bookingQueueDetails: bookingQueueDetails || [] };

  const descriptors = collectQueueStepDescriptors({
    questionnaire,
    questionAnswers,
  });

  return {
    bookingQueueDetails: applyQueueStepDescriptors(
      bookingQueueDetails,
      descriptors,
      context,
    ),
  };
};
